/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { Link, Image } from '@ta-interaktiv/contentbricks'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { influenza } from './influenza'

// TODO Set the correct article ID here
const ARTICLE_ID = '919329498926'

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical very fitted segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>
              100 Jahre ohne schlimme Pandemie – die Schweizer haben eine
              Katastrophen-Gedächtnislücke
            </h1>
            <h2>
              Seit der Spanischen Grippe wurde die Schweiz von tödlichen
              Infektionskrankheiten weitgehend verschont. Welche Auswirkungen
              eine Pandemie haben kann, hatte die Bevölkerung vergessen – bis
              die aktuelle Corona-Krise ausbrach.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical fitted segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Yannick Wiget, Patrick Vögeli, Marc Brupbacher
            </div>
            <div className='ta updated'>Aktualisiert am 1. Mai 2021</div>
          </div>
          <div className='ta text'>
            Schon mehr als 10’000 Menschen sind in der Schweiz im Zusammenhang
            mit einer Corona-Infektion gestorben. Damit gehört die aktuelle
            Pandemie zu den schlimmsten in der Geschichte des Landes. Das zeigt
            ein Vergleich mit allen Grippewellen der letzten 144 Jahre: Setzt
            man die Zahl der Todesfälle ins Verhältnis zur Bevölkerung des
            jeweiligen Jahres, wurde Covid-19 nur von der Spanischen Grippe vor
            mehr als hundert Jahren übertroffen.
          </div>
        </div>
      </div>

      <div className='ui vertical fitted segment'>
        {browserCheck.isMobile ? (
          <div className='ui container'>
            <div className='column special overview'>
              <div className='titleBox'>
                <h4 className='graphicTitle'>
                  Covid-19 im Vergleich mit Grippe-Pandemien in der Schweiz,
                  1877–2021
                </h4>
                <div className='graphicSub'>
                  Todesfälle verursacht durch Influenza- oder Coronaviren pro
                  100'000&nbsp;Einwohner
                </div>
              </div>
              <div className='graphicLegend'>
                <div className='itemWrapper'>
                  <div className='square red'></div>
                  <div className='legendItem'>Grippe</div>
                </div>
                <div className='itemWrapper'>
                  <div className='square blue'></div>
                  <div className='legendItem'>Corona</div>
                </div>
              </div>
              <div className='graphicBox'>
                <Image
                  file={require('../../images/pandemien-vergleich.svg')}
                  alt='Covid-19 im Vergleich mit Grippe-Pandemien in der Schweiz'
                  className='cbImage pandemienVergleich'
                  // source='Quelle: Meteo Schweiz'
                />
                <div className='sourceTwo'>
                  <span>
                    Hinweis: Sars (2003/04) und Covid-19 (2020/21) wurden nicht
                    durch Influenza-, sondern Coronaviren verursacht.
                  </span>
                  <br />
                  Quellen: Historische Statistik der Schweiz, Bundesamt für
                  Statistik, Die Influenza in der Schweiz in den Jahren
                  1889-1894
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='ui container'>
            <div className='column special overview'>
              <div className='titleBox'>
                <h4 className='graphicTitle'>
                  Covid-19 im Vergleich mit Grippe-Pandemien in der Schweiz,
                  1877–2021
                </h4>
                <div className='graphicSub'>
                  Todesfälle verursacht durch Influenza- oder Coronaviren pro
                  100'000&nbsp;Einwohner
                </div>
              </div>
              <div className='graphicLegend'>
                <div className='itemWrapper'>
                  <div className='square red'></div>
                  <div className='legendItem'>Grippe</div>
                </div>
                <div className='itemWrapper'>
                  <div className='square blue'></div>
                  <div className='legendItem'>Corona</div>
                </div>
              </div>
              <div className='graphicBox'>
                <div className='boxWrapper'>
                  <div className='iframeBox'>
                    <AutofitIFrame
                      src='https://datawrapper.dwcdn.net/U5rfr/'
                      style={{ height: '100%' }}
                    />
                  </div>
                </div>
                <div className='sourceTwo'>
                  <span>
                    Hinweis: Sars (2003/04) und Covid-19 (2020/21) wurden nicht
                    durch Influenza-, sondern Coronaviren verursacht.
                  </span>
                  <br />
                  Quellen: Historische Statistik der Schweiz, Bundesamt für
                  Statistik, Die Influenza in der Schweiz in den Jahren
                  1889-1894
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className='ui vertical fitted segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <p>
              Seit 1877 erhebt das{' '}
              <Link href='https://www.bfs.admin.ch/bfs/de/home/statistiken/gesundheit/gesundheitszustand/sterblichkeit-todesursachen/spezifische.html'>
                Bundesamt für Statistik
              </Link>{' '}
              (früher{' '}
              <Link href='https://hsso.ch/2012/d/34' allowWordWrap>
                Eidgenössisches Statistisches Büro
              </Link>{' '}
              ) die Todesfälle in der Schweiz nach Ursachen. Allerdings war die
              Grippe, auch Influenza genannt, Ende 19. Jahrhundert noch nicht
              Teil des obligatorischen Todesursachen-Katalogs. Die Zahl der
              Opfer durch die Russische Grippe haben wir daher dem{' '}
              <Link
                href='https://digital.zbmed.de/gesundheitspflege/content/pageview/5848546'
                allowWordWrap
              >
                Werk von Friedrich Schmid
              </Link>{' '}
              aus dem Jahr 1895 entnommen. Gemäss dem damaligen Chef des
              Statistischen Büros sind auf dem Höhepunkt jener Pandemie 1890
              laut ärztlicher Bescheinigung 2624 Menschen an Influenza
              gestorben.
            </p>
            <p>
              In der offiziellen Statistik wurden damals nur einige Hundert
              Todesfälle durch die Grippe erfasst. Hier zeigt sich die
              Schwierigkeit historischer Vergleiche: Je nachdem, welche Quelle
              man heranzieht, hat die erste Russische Grippe fast gleich viele
              oder deutlich weniger Opfer gefordert als Covid-19. Sicher ist:
              Die Schweiz blieb von vielen Pandemien mehr oder weniger verschont
              – umso härter trifft sie nun die aktuelle Krise, wie unsere
              Übersicht zeigt:
            </p>
          </div>
        </div>
      </div>

      {influenza.map((i, index) => (
        <>
          <div className='ui vertical fitted segment'>
            <div className='ui text container'>
              <h4 className='chapter'>
                <span className='year'>{i.date}</span>
                <br />
                {i.typ}
              </h4>{' '}
              <div className='influenzaDashboard'>
                <div className='row one'>
                  <div className='oneColumn'>
                    <div className='title darkBlue'>
                      Infektionen{' '}
                      {/* <img
                        src={require('../../images/infection.svg')}
                        className='icon'
                      ></img> */}
                    </div>
                    <div className='contentWrapper'>
                      <div className='infectionColumn'>
                        <div className='secondWrapper'>
                          <div className='number'>{i.casesCH}</div>
                          <div className='byline'>Schweiz</div>
                        </div>
                      </div>
                      <div className='infectionColumn'>
                        <div className='secondWrapper'>
                          <div className='number'>{i.casesWorld}</div>
                          <div className='byline'>weltweit</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='row two'>
                  <div className='twoColumn'>
                    <div className={i.influenza ? 'title red' : 'title blue'}>
                      Todesfälle{' '}
                      {/* <img
                        src={
                          i.influenza
                            ? require('../../images/death-red.svg')
                            : require('../../images/death-blue.svg')
                        }
                        className='icon'
                      ></img> */}
                    </div>
                    <div className='contentWrapper'>
                      <div
                        className={
                          i.influenza ? 'deathColumn red' : 'deathColumn blue'
                        }
                      >
                        <div className='secondWrapper'>
                          <div className='number'>{i.deathsCH}</div>
                          <div className='byline'>Schweiz</div>
                        </div>
                      </div>
                      <div
                        className={
                          i.influenza ? 'deathColumn red' : 'deathColumn blue'
                        }
                      >
                        <div className='secondWrapper'>
                          <div className='number'>{i.deathsWorld}</div>
                          <div className='byline'>weltweit</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>{i.content}</div>
          </div>
        </>
      ))}

      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie gefällt Ihnen die Gestaltung?'
        ]}
        credits={[
          [
            'Konzept und Umsetzung',
            'Yannick Wiget, Patrick Vögeli, Marc Brupbacher'
          ]
        ]}
      />
    </div>
  )
}
